import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, downloadUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/I18nContext";
import { Button } from "../components/ui/button";
import { Download, FileText, ImageIcon, Trash2, ArrowRight } from "lucide-react";
import BuyCreditsButton from "../components/BuyCreditsButton";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const t = useT();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/documents");
      setDocs(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Delete?")) return;
    try {
      await api.delete(`/documents/${id}`);
      toast.success(t("toast.deleted"));
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="paper-bg min-h-[calc(100vh-4rem)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="dashboard-page">

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <div className="pill mb-3">{t("dashboard.badge")}</div>
            <h1 className="font-serif-display text-4xl font-bold text-[#0F172A]">
              {user?.name ? t("dashboard.helloWithName", { name: user.name }) : t("dashboard.helloDefault")}
            </h1>
            <p className="text-[#475569] mt-1" data-testid="dashboard-credits-line">
              {t("dashboard.creditsLine", { credits: user?.credits ?? 0, ops: user?.operations_used ?? 0 })}
            </p>
          </div>
          <BuyCreditsButton onSuccess={refresh}/>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-10">
          <Link to="/tools/image-to-pdf" data-testid="dashboard-tool-image"
            className="card-classic p-6 hover:shadow-lg transition-all group">
            <div className="flex items-center justify-between">
              <div className="w-11 h-11 rounded-md bg-[#0F172A] text-white grid place-items-center group-hover:bg-[#881337] transition-colors">
                <ImageIcon size={18}/>
              </div>
              <ArrowRight className="text-[#64748B] group-hover:text-[#0F172A] rtl:rotate-180"/>
            </div>
            <h3 className="mt-4 font-serif-display text-2xl font-semibold">{t("landing.tool.image.title")}</h3>
            <p className="text-[#475569] mt-1">{t("landing.tool.image.copy")}</p>
          </Link>
          <Link to="/tools/cv-builder" data-testid="dashboard-tool-cv"
            className="card-classic p-6 hover:shadow-lg transition-all group">
            <div className="flex items-center justify-between">
              <div className="w-11 h-11 rounded-md bg-[#0F172A] text-white grid place-items-center group-hover:bg-[#881337] transition-colors">
                <FileText size={18}/>
              </div>
              <ArrowRight className="text-[#64748B] group-hover:text-[#0F172A] rtl:rotate-180"/>
            </div>
            <h3 className="mt-4 font-serif-display text-2xl font-semibold">{t("landing.tool.cv.title")}</h3>
            <p className="text-[#475569] mt-1">{t("landing.tool.cv.copy")}</p>
          </Link>
        </div>

        <div className="card-classic p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif-display text-2xl font-semibold">{t("dashboard.savedTitle")}</h2>
            <span className="pill">{t("dashboard.savedTotal", { n: docs.length })}</span>
          </div>
          {loading ? (
            <div className="text-sm text-[#64748B]">{t("common.loading")}</div>
          ) : docs.length === 0 ? (
            <div className="text-sm text-[#64748B]" data-testid="saved-empty-state">
              {t("dashboard.empty")}
            </div>
          ) : (
            <div className="overflow-x-auto" data-testid="saved-cv-list-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left rtl:text-right border-b border-[#E2DBCE] text-[#64748B] text-xs uppercase tracking-wider font-mono-jb">
                    <th className="py-2">{t("dashboard.col.title")}</th>
                    <th className="py-2">{t("dashboard.col.type")}</th>
                    <th className="py-2">{t("dashboard.col.created")}</th>
                    <th className="py-2 text-right rtl:text-left">{t("dashboard.col.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map((d) => (
                    <tr key={d.id} className="border-b border-[#F4F0EA]" data-testid={`saved-row-${d.id}`}>
                      <td className="py-3 font-medium text-[#0F172A]">{d.title}</td>
                      <td className="py-3">
                        <span className="pill">
                          {d.kind === "cv" ? t("dashboard.type.cv") : t("dashboard.type.image")}
                        </span>
                      </td>
                      <td className="py-3 text-[#64748B]">
                        {d.created_at ? new Date(d.created_at).toLocaleString() : "—"}
                      </td>
                      <td className="py-3 text-right rtl:text-left">
                        <a href={downloadUrl(`/api/documents/${d.id}/download`)} target="_blank" rel="noreferrer">
                          <Button size="sm" variant="outline" className="mx-1 border-[#E2DBCE]" data-testid={`download-saved-pdf-button-${d.id}`}>
                            <Download size={14} className="mr-1"/> {t("common.download")}
                          </Button>
                        </a>
                        <Button size="sm" variant="ghost" onClick={() => onDelete(d.id)} data-testid={`delete-doc-${d.id}`}>
                          <Trash2 size={14} className="text-[#DC2626]"/>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
